import React, { useEffect, useRef, useState } from 'react'

// How loud one course chat is allowed to be. Mirrors ChannelState::LEVELS,
// and the descriptions are the only place a member is told what the daily
// digest actually is, so keep them concrete.
export const NOTIFY_LEVELS = [
  { value: 'all',      label: 'All activity',  description: 'Mentions, plus a daily summary of what you missed' },
  { value: 'mentions', label: 'Mentions only', description: 'Only when you or a group you\'re in is mentioned' },
  { value: 'none',     label: 'Nothing',       description: 'No notifications or emails from this chat' },
]

const BellIcon = ({ className }) => (
  <svg viewBox='0 0 20 20' fill='currentColor' className={className} aria-hidden='true'>
    <path fillRule='evenodd' d='M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a2 2 0 01-2-2h4a2 2 0 01-2 2z' clipRule='evenodd' />
  </svg>
)

const BellOffIcon = ({ className }) => (
  <svg viewBox='0 0 20 20' fill='currentColor' className={className} aria-hidden='true'>
    <path d='M13.28 3.22a.75.75 0 10-1.06 1.06l1.372 1.373A5.98 5.98 0 0114 8v2.28l1.5 1.5V8a7.47 7.47 0 00-1.06-3.84l-1.16-.94zM4 8a6 6 0 018.9-5.24L3.22 12.44A5.97 5.97 0 014 10V8zm-1.78-4.78a.75.75 0 011.06 0l13.5 13.5a.75.75 0 11-1.06 1.06L2.22 4.28a.75.75 0 010-1.06zM8 17a2 2 0 003.86.73L8 14.87V17z' />
  </svg>
)

// The same control in the channel header and on every row of the chat index,
// because the room you want to quiet is usually one you are not sitting in.
//
// `compact` drops the text label for the index rows, where the course name is
// already doing that work.
const NotifyLevelMenu = ({ level = 'all', onChange, compact = false, align = 'right' }) => {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)

  useEffect(() => {
    if (!open) return undefined
    const onDown = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false) }
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const current = NOTIFY_LEVELS.find(l => l.value === level) || NOTIFY_LEVELS[0]
  const silenced = level === 'none'
  const Icon = silenced ? BellOffIcon : BellIcon

  const pick = (value) => {
    setOpen(false)
    if (value !== level) onChange(value)
  }

  return (
    <div className='relative' ref={wrapRef}>
      <button
        type='button'
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(o => !o) }}
        aria-haspopup='menu'
        aria-expanded={open}
        aria-label={`Notifications: ${current.label}`}
        title={`Notifications: ${current.label}`}
        className={`inline-flex items-center gap-1.5 rounded-lg text-xs font-semibold transition-colors ${
          compact ? 'p-1.5' : 'px-2.5 py-1'
        } ${
          silenced
            ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900/60'
            : 'text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700'
        }`}
      >
        <Icon className='h-4 w-4' />
        {!compact && <span className='hidden sm:inline'>{current.label}</span>}
      </button>

      {open && (
        <div
          role='menu'
          className={`absolute z-40 mt-1 w-64 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl p-1 ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          <div className='px-3 pt-2 pb-1 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400'>
            Notify me about
          </div>
          {NOTIFY_LEVELS.map(option => (
            <button
              key={option.value}
              type='button'
              role='menuitemradio'
              aria-checked={option.value === level}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); pick(option.value) }}
              className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                option.value === level
                  ? 'bg-clcpurple/10'
                  : 'hover:bg-slate-50 dark:hover:bg-slate-800'
              }`}
            >
              <span className={`block text-sm font-semibold ${
                option.value === level
                  ? 'text-clcpurple-dark dark:text-clcpurple-light'
                  : 'text-slate-900 dark:text-slate-100'
              }`}>
                {option.label}
              </span>
              <span className='block text-xs text-slate-500 dark:text-slate-400'>{option.description}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default NotifyLevelMenu
